import { useQuery } from '@apollo/client';
import { EnterpriseProgramMemberships_ProgramMembershipState as ProgramMembershipState } from '__generated__/graphql-types';

import { getClientName } from 'bundles/cms/utils/SpaceUtils';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import { orgLandingContentfulQuery } from 'bundles/org-landing/components/OrgLandingContentfulQuery';
import { orgLandingProgramsQuery } from 'bundles/org-landing/components/OrgLandingProgramsQuery';
import type {
  OrgLandingProgramsQuery,
  OrgLandingProgramsQueryVariables,
  OrgLandingProgramsQuery_EnterpriseLearnerSummariesV1Resource_byUserIdAndThirdPartyOrgSlug_elements_enterprisePrograms_elements,
  OrgLandingProgramsQuery_EnterpriseLearnerSummariesV1Resource_byUserIdAndThirdPartyOrgSlug_elements_invitations_elements,
} from 'bundles/org-landing/components/__generated__/OrgLandingProgramsQuery';
import type {
  OrgLandingContentfulQuery,
  OrgLandingContentfulQueryVariables,
} from 'bundles/org-landing/components/__generated__/contentfulTypes';
import QueryDeletedProgramMembershipsByUserId from 'bundles/org-landing/components/queries/QueryDeletedProgramMembershipsByUserId.graphql';
import QueryProgramMembershipsByUserIdAndIncludeInvitations from 'bundles/org-landing/components/queries/QueryProgramMembershipsByUserIdAndIncludeInvitations.graphql';
import type {
  QueryDeletedProgramMembershipsByUserIdQuery,
  QueryDeletedProgramMembershipsByUserIdQueryVariables,
} from 'bundles/org-landing/components/queries/__generated__/QueryDeletedProgramMembershipsByUserId';
import type {
  QueryProgramMembershipsByUserIdAndIncludeInvitationsQuery,
  QueryProgramMembershipsByUserIdAndIncludeInvitationsQueryVariables,
} from 'bundles/org-landing/components/queries/__generated__/QueryProgramMembershipsByUserIdAndIncludeInvitations';

type Options = {
  orgSlug: string;
  orgId: string;
  userId?: number;
  locale?: string;
  isPreview?: boolean;
  skip?: boolean;
};

export type EnterpriseProgram =
  OrgLandingProgramsQuery_EnterpriseLearnerSummariesV1Resource_byUserIdAndThirdPartyOrgSlug_elements_enterprisePrograms_elements;

export type ProgramInvitation =
  OrgLandingProgramsQuery_EnterpriseLearnerSummariesV1Resource_byUserIdAndThirdPartyOrgSlug_elements_invitations_elements;

export type SoftDeletedProgramEdge = NonNullable<
  NonNullable<
    QueryDeletedProgramMembershipsByUserIdQuery['ProgramMemberships']
  >['queryDeletedProgramMembershipsByUserId']['edges']
>[number];

export type SoftDeletedProgramNode = NonNullable<SoftDeletedProgramEdge>['node'];

export type ProgramMembership = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        QueryProgramMembershipsByUserIdAndIncludeInvitationsQuery['ProgramMemberships']
      >['queryProgramMembershipsByUserIdAndIncludeInvitations']['edges']
    >[number]
  >['node']
>;

export type OutProps = {
  enterprisePrograms: Array<EnterpriseProgram>;
  softDeletedPrograms: Array<SoftDeletedProgramNode>;
  programInvitations: Array<ProgramInvitation>;
  membershipsMap: Record<string, ProgramMembership>;
  contentfulData?: OrgLandingContentfulQuery;
  isLoading: boolean;
};

function useQueryProgramMembershipsByUserIdAndIncludeInvitations({ userId }: { userId: number | undefined }) {
  const { data, loading } = useQuery<
    QueryProgramMembershipsByUserIdAndIncludeInvitationsQuery,
    QueryProgramMembershipsByUserIdAndIncludeInvitationsQueryVariables
  >(QueryProgramMembershipsByUserIdAndIncludeInvitations, {
    skip: !userId,
    variables: {
      userId: userId ?? 0,
      includeInvitations: true,
    },
    context: { clientName: 'gatewayGql' },
  });

  return { data, loading };
}

function useQueryDeletedProgramMembershipsByUserIdQuery({ userId }: { userId: number | undefined }) {
  const { data, loading } = useQuery<
    QueryDeletedProgramMembershipsByUserIdQuery,
    QueryDeletedProgramMembershipsByUserIdQueryVariables
  >(QueryDeletedProgramMembershipsByUserId, {
    skip: !userId,
    variables: {
      userId: userId ?? 0,
    },
    context: { clientName: 'gatewayGql' },
  });

  return { data, loading };
}

function useOrgLandingQuery({
  orgSlug,
  orgId,
  userId,
  locale = 'en',
  isPreview = false,
  skip = false,
}: Options): OutProps {
  const { data: contentfulData, loading: contentfulLoading } = useQuery<
    OrgLandingContentfulQuery,
    OrgLandingContentfulQueryVariables
  >(orgLandingContentfulQuery, {
    variables: {
      orgSlug,
      preview: isPreview,
      locale,
    },
    skip,
    context: { clientName: getClientName('ENTERPRISE', isPreview) },
  });

  const { data: programsData, loading: isProgramsDataLoading } = useQuery<
    OrgLandingProgramsQuery,
    OrgLandingProgramsQueryVariables
  >(orgLandingProgramsQuery, {
    skip: !userId,
    variables: {
      userId: (userId ?? '').toString(),
      thirdPartyOrgSlug: orgSlug,
    },
  });

  const enterprisePrograms = filterExistsOrDefault(
    programsData?.EnterpriseLearnerSummariesV1Resource?.byUserIdAndThirdPartyOrgSlug?.elements?.[0]?.enterprisePrograms
      ?.elements
  );
  const programInvitations = filterExistsOrDefault(
    programsData?.EnterpriseLearnerSummariesV1Resource?.byUserIdAndThirdPartyOrgSlug?.elements?.[0]?.invitations
      ?.elements
  );

  const { data: programMembershipData, loading: isProgramMembershipLoading } =
    useQueryProgramMembershipsByUserIdAndIncludeInvitations({ userId });

  const programInvitationIds = programInvitations
    .map((invitation) => invitation?.userRoster?.program?.id)
    .filter((v) => !!v) as string[];
  const { data: deletedProgramMembershipData, loading: isDeletedProgramMembershipLoading } =
    useQueryDeletedProgramMembershipsByUserIdQuery({ userId });

  // This is a workaround to get the soft deleted programs.
  // We don't have an optimal way to get soft deleted programs for a user, with program, organization, of a specific state.
  // So this is a workaround to filter on the client side and filter based on what we need.
  const softDeletedProgramsRaw =
    deletedProgramMembershipData?.ProgramMemberships?.queryDeletedProgramMembershipsByUserId?.edges?.filter((edge) => {
      const membership = edge?.node;
      return (
        edge?.node &&
        membership?.program?.naptimeThirdPartyOrganization?.id === orgId &&
        membership?.state === ProgramMembershipState.ProgramMembershipStateSoftDeletedMember &&
        programInvitationIds.includes(membership?.program?.id) === false
      );
    });

  const softDeletedPrograms: SoftDeletedProgramNode[] = (softDeletedProgramsRaw ?? []).map((edge) => {
    return edge?.node;
  });

  const isLoading =
    contentfulLoading || isProgramsDataLoading || isProgramMembershipLoading || isDeletedProgramMembershipLoading;

  const membershipsMap: Record<string, ProgramMembership> = {};
  if (!isLoading) {
    for (const membership of programMembershipData?.ProgramMemberships
      ?.queryProgramMembershipsByUserIdAndIncludeInvitations?.edges ?? []) {
      if (membership?.node && membership.node.program?.id) {
        membershipsMap[membership.node.program.id] = membership.node as ProgramMembership;
      }
    }
  }
  return { enterprisePrograms, softDeletedPrograms, programInvitations, membershipsMap, contentfulData, isLoading };
}

export default useOrgLandingQuery;
